import React, { useEffect, useRef, useState } from "react";
import ImageUploading from "react-images-uploading";
import ImagesCardPost from "./ImagesCardPost";
import { FiSmile } from "react-icons/fi";
import EmojiPicker from "emoji-picker-react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { axiosInstanceWithToken } from "@/helpers/axios";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

// Define Yup validation schema
const schema = Yup.object().shape({
    reason: Yup.string().required("Reason is required"),
    amount: Yup.number().required("Amount is required").positive("Amount must be positive"),
    situation_description: Yup.string().required("Situation description is required"),
});

const BtnModalPost = ({
    images,
    setImages,
    postValueInput,
    setOPenPost = () => { },
}) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const location = useLocation();

    const { row, isHost = false } = location.state;
    // Initialize react-hook-form with validation schema
    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            reason: postValueInput?.reason || '',  // Set default value from state or empty string
            amount: postValueInput?.amount || 0,  // Set default value from state or empty string
            situation_description: postValueInput?.situation_description || '',  // Set default value from state or empty string
        }
    });

    // Create ref to track the emoji picker container
    const emojiPickerRef = useRef(null);

    // Close emoji picker if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);  // Close picker if click is outside
            }
        };

        // Attach event listener
        document.addEventListener("mousedown", handleClickOutside);

        // Clean up event listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClose = () => setOPenPost(false);

    const handleSave = (data) => {
        console.log("Saved Data: ", data);

        const cleanedImages = images.map((image) => {
            const { data_url, ...rest } = image; // Destructure to remove data_url
            return rest;
        });

        let formData = new FormData();

        // Assuming cleanedImages is an array of File objects
        cleanedImages.forEach((image) => {
            // Append each image as binary data to the form data
            formData.append('images[]', image?.file); // image.name is optional, but helps in specifying file nam
            return image;
        });

        // formData.append('images[]', cleanedImages);
        formData.append('reason', data?.reason);
        formData.append('amount', data?.amount);
        formData.append('situation_description', data?.situation_description);
        formData.append('status', 'pending');
        formData.append('booking_id', row?.id);

        console.log("Saved Images: ", cleanedImages); // This will log the images without data_url

        axiosInstanceWithToken.post("/post-checkout-charges", formData).then((res) => {
            console.log(res)
            if (res?.data?.status)
                Swal.fire({
                    text: 'Post created successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK'
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleClose();
                        window.location.reload();
                        // window.location.href = '/dashboard';
                    }
                });
            else
                Swal.fire({
                    text: 'Failed to create post. Please try again later.',
                    icon: 'error',
                });
        }).catch((error) => {
            console.error(error);
            Swal.fire({
                text: 'Failed to create post. Please try again later.',
                icon: 'error',
            });
        }).finally(() => {
            handleClose();
            setImages([]); // Reset images state
        })


    };

    const maxNumber = 70;

    const onChange = (imageList) => {
        setImages(imageList);
    };

    const handleDelete = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
    };

    const handleEmojiClick = (emojiObject) => {
        setValue("situation_description", watch("situation_description") + emojiObject.emoji); // Update emoji in the input
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
        >
            <div className="w-full max-w-2xl overflow-hidden bg-white rounded-lg shadow-lg">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h2 className="text-xl font-semibold">Checkout Fee Details</h2>
                </div>
                <div className="px-6 py-4 overflow-y-auto max-h-[480px]">
                    <div className="space-y-4">
                        <form onSubmit={handleSubmit(handleSave)}>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block mb-1 font-medium text-gray-700">
                                        Reason
                                    </label>
                                    <input
                                        type="text"
                                        {...register("reason")}
                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {errors.reason && (
                                        <p className="text-sm text-red-500">{errors.reason.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block mb-1 font-medium text-gray-700">
                                        Amount Requested
                                    </label>
                                    <input
                                        type="number"
                                        {...register("amount")}
                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {errors.amount && (
                                        <p className="text-sm text-red-500">{errors.amount.message}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <ImageUploading
                                    multiple
                                    value={images}
                                    onChange={onChange}
                                    maxNumber={maxNumber}
                                    dataURLKey="data_url"
                                    acceptType={["jpg", "jpeg", "png", "webp"]}
                                >
                                    {({ imageList, onImageUpload, isDragging, dragProps }) => (
                                        <div className="space-y-2">
                                            <button
                                                className={`px-4 py-2 rounded ${isDragging ? "bg-red-400" : "bg-[#8ec639]"} text-white hover:bg-green-500`}
                                                onClick={onImageUpload}
                                                {...dragProps}
                                            >
                                                Upload Images
                                            </button>
                                            <div className="flex flex-wrap gap-4">
                                                {imageList.map((image, index) => (
                                                    <ImagesCardPost
                                                        key={index}
                                                        image={image}
                                                        index={index}
                                                        onDelete={handleDelete}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </ImageUploading>
                            </div>
                            <div>
                                <label className="block mb-1 font-medium text-gray-700">
                                    Describe the situation to your guest
                                </label>
                                <div className="relative flex flex-col items-center">
                                    <div className="flex w-full">
                                        <input
                                            type="text"
                                            {...register("situation_description")}
                                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <FiSmile
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            className="ml-2 text-gray-500 cursor-pointer hover:text-gray-800"
                                            size={24}
                                        />
                                        {showEmojiPicker && (
                                            <div
                                                ref={emojiPickerRef}
                                                className="fixed top-0 !right-[25%] z-50 mt-2 bg-white rounded-lg shadow-lg">
                                                <EmojiPicker onEmojiClick={handleEmojiClick} />
                                            </div>
                                        )}
                                    </div>
                                    {errors.situation_description && (
                                        <p className="text-sm text-red-500">{errors.situation_description.message}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between px-6 py-3 border-t">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-[#8ec639] rounded hover:bg-green-500 focus:outline-none"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BtnModalPost;
